import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import { api, isApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query';

import { isIsoDate, isoDateToReadInstant, todayInSaoPaulo } from '@/features/pricing';

import type {
  CatalogItem,
  CatalogItemPage,
  CatalogPrice,
  CatalogPriceList,
  CategoryList,
  EntityStatus,
  UnitConversion,
  UnitConversionList,
  UnitOfMeasurePage,
} from './contracts';
import { indexUnits, type UnitIndex } from './conversions';

const CATALOG = '/v1/catalog';

/**
 * The price is a separate question from the catalogue listing, and a separate question from a
 * quotation.
 *
 * `/v1/catalog/items` returns the curated entry and nothing else: it takes no date and
 * denormalises no price. The price is read from `/v1/catalog/prices`, which returns the **price
 * table published to the caller's own organisation** — a `GET`, on the tenant connection, keyed by
 * the SKUs on screen and the instant to read them at.
 *
 * This used to `POST /v1/pricing/quotes`, and that was the defect. The quote route opens the
 * database session that can decrypt acquisition cost, so RNF01 requires it to refuse every
 * customer profile — which it does: a `buyer` and an `operator`, the two people the catalogue
 * exists for, got 403 and saw no price at all. The published table is the path the schema was
 * designed for: the engine computes behind the boundary and publishes, and the customer reads what
 * was published over a connection that has no access to cost in the first place.
 */
const CATALOG_PRICES = `${CATALOG}/prices`;

/** Page size chosen so the first screenful of the grid arrives in one request on a laptop. */
const PAGE_SIZE = 24;

/** Units are the most stable reference data in the product — fetched whole, once. */
const REFERENCE_LIMIT = 200;

export interface CatalogFilters {
  readonly search: string;
  readonly category: string;
  readonly subcategory: string;
  readonly status: string;
}

export const EMPTY_CATALOG_FILTERS: CatalogFilters = {
  search: '',
  category: '',
  subcategory: '',
  status: '',
};

/** An empty filter is absent from the query string, not sent as `""`. */
function toQueryParams(filters: CatalogFilters, cursor: string | undefined) {
  return {
    limit: PAGE_SIZE,
    ...(filters.search === '' ? {} : { search: filters.search }),
    ...(filters.category === '' ? {} : { category: filters.category }),
    ...(filters.subcategory === '' ? {} : { subcategory: filters.subcategory }),
    ...(filters.status === '' ? {} : { status: filters.status as EntityStatus }),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

interface TenantBranch {
  readonly key: string;
  readonly enabled: boolean;
}

/**
 * No request carries the tenant: the API derives it from the `tenant_id` claim and Row Level
 * Security filters the rows. The claim is still part of every cache key so a second sign-in on
 * the same browser cannot read the first tenant's cached catalogue.
 */
function useTenantBranch(): TenantBranch {
  const { tenantId } = useAuth();
  return { key: tenantId ?? 'sem-tenant', enabled: tenantId !== null };
}

/**
 * The grid.
 *
 * The reference date is deliberately *not* part of this key: the catalogue listing does not carry
 * a price and does not take a date, so keying it by one would refetch the same entries every time
 * the operator moved the date control. The price is a second query — `useCatalogPricesQuery` —
 * and that is the one the date belongs to.
 */
export function useCatalogItemsQuery(
  filters: CatalogFilters,
  /** `false` holds the request — the header search, until three letters are typed. */
  active = true,
): UseInfiniteQueryResult<{ pages: CatalogItemPage[] }, Error> {
  const { key, enabled } = useTenantBranch();

  return useInfiniteQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'items', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<CatalogItemPage>(`${CATALOG}/items`, {
        query: toQueryParams(filters, pageParam),
        signal,
      }),
    // `has_more` is the authority, not a non-empty `next_cursor`: a backend that returned a
    // cursor on the last page would otherwise make the grid ask for an empty page forever.
    getNextPageParam: (lastPage) =>
      lastPage.page?.has_more === true ? (lastPage.page.next_cursor ?? undefined) : undefined,
    enabled: enabled && active,
  });
}

export function useCatalogCategoriesQuery(): UseQueryResult<readonly string[], Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'categories'],
    queryFn: async ({ signal }) => {
      const response = await api.get<CategoryList>(`${CATALOG}/categories`, { signal });
      return response.categories;
    },
    // Categories change on the order of weeks; refetching them with every filter change would
    // spend requests on an answer that is almost always identical.
    staleTime: 10 * 60_000,
    enabled,
  });
}

export function useCatalogItemQuery(skuId: string | undefined): UseQueryResult<CatalogItem, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'item', skuId ?? ''],
    queryFn: ({ signal }) =>
      api.get<CatalogItem>(`${CATALOG}/items/${encodeURIComponent(skuId ?? '')}`, { signal }),
    enabled: enabled && skuId !== undefined && skuId !== '',
  });
}

/**
 * The published sale price of the SKUs currently on screen, for the caller's own organisation.
 *
 * Looked up by SKU id. A SKU **missing** from `lines` has no published price covering the date,
 * which the screens render as the absent-price sentence rather than as zero. The API cannot say
 * which of "no table covers it" and "it is priced for a different customer" is the reason — the
 * two are deliberately indistinguishable, so that this route cannot be used to enumerate what
 * other customers are offered — so the SPA has exactly one absence to handle.
 */
export interface PriceIndex {
  readonly lines: ReadonlyMap<string, CatalogPrice>;
  /** The instant the API actually read the tables at, echoed back as `YYYY-MM-DD`. */
  readonly referenceDate: string;
}

/**
 * Turn the API's echoed instant into the day it belongs to, in São Paulo.
 *
 * Named in São Paulo and not in UTC: an instant at 22:00 in Brasília is still today, and
 * labelling it tomorrow would misdate the price on the card.
 */
function labelDate(echoed: string, fallback: string): string {
  const read = new Date(echoed);
  return Number.isNaN(read.getTime()) ? fallback : todayInSaoPaulo(read);
}

/**
 * The instant to read the tables at, as the API takes it.
 *
 * A price table's validity is an *instant*, not a calendar day, so the day has to be turned
 * into one -- and which moment of the day is chosen decides what the customer sees. The start
 * of it is the wrong answer here: a table published this afternoon was genuinely not in force
 * at midnight, so a catalogue asking about midnight reports every price as unpublished for the
 * rest of the day it was published on. `isoDateToReadInstant` asks about now instead.
 */
function readAt(referenceDate: string): Record<string, string> {
  return isIsoDate(referenceDate) ? { at: isoDateToReadInstant(referenceDate) } : {};
}

/**
 * The published price of a set of SKUs — the grid.
 *
 * Separate from the listing because the API keeps them separate: no catalogue route denormalises
 * a price. `referenceDate` is part of the cache key — the same SKUs on two different dates are two
 * different answers, and sharing one entry between them would show yesterday's price under today's
 * heading.
 *
 * It lives on the `catalogBrowse` branch so that publishing a price table, which invalidates that
 * branch, drops these too: a buyer who reloaded into a cached answer would be shown a price from
 * the table that has since been superseded.
 */
export function useCatalogPricesQuery(
  skuIds: readonly string[],
  referenceDate: string,
): UseQueryResult<PriceIndex, Error> {
  const { key, enabled } = useTenantBranch();
  // Sorted so that the same set of SKUs in a different order is the same cache entry rather than a
  // second request for an identical answer.
  const ids = [...skuIds].sort();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'prices', referenceDate, ids],
    queryFn: async ({ signal }) => {
      // No tenant in the request: the API takes it from the `tenant_id` claim and row level
      // security decides which rows exist. A customer id in a query string would be a request
      // the server must then be trusted to ignore.
      const published = await api.get<CatalogPriceList>(CATALOG_PRICES, {
        query: { sku_id: ids, ...readAt(referenceDate) },
        signal,
      });

      return {
        lines: new Map(published.prices.map((price) => [price.sku_id, price])),
        referenceDate: labelDate(published.reference_date, referenceDate),
      } satisfies PriceIndex;
    },
    enabled: enabled && ids.length > 0,
  });
}

/** One SKU's published price, or `null` when none is published for the caller on that date. */
export interface SkuPrice {
  readonly price: CatalogPrice | null;
  readonly referenceDate: string;
}

/**
 * The published price of one SKU — the detail page.
 *
 * `GET /v1/catalog/items/{sku_id}/price` answers 404 when no published table covers the SKU on
 * that date, and that is a **normal state, not a failure**: it is what the screen must render as
 * "price not published". So the 404 is caught here and turned into `price: null`, leaving the
 * query's error state for failures a reader can actually do something about. Every other status is
 * left to propagate.
 */
export function useSkuPriceQuery(
  skuId: string | undefined,
  referenceDate: string,
): UseQueryResult<SkuPrice, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'price', skuId ?? '', referenceDate],
    queryFn: async ({ signal }) => {
      try {
        const price = await api.get<CatalogPrice>(
          `${CATALOG}/items/${encodeURIComponent(skuId ?? '')}/price`,
          { query: readAt(referenceDate), signal },
        );
        return { price, referenceDate } satisfies SkuPrice;
      } catch (error) {
        if (isApiError(error) && error.status === 404) {
          return { price: null, referenceDate } satisfies SkuPrice;
        }
        throw error;
      }
    },
    // A 404 is answered above rather than retried, and every other failure here is a failure
    // retrying cannot fix any faster than the shared policy already does.
    enabled: enabled && skuId !== undefined && skuId !== '',
  });
}

/**
 * The catalogue returns `base_unit_id`, not an embedded unit, so every screen that shows a
 * quantity needs this index. Fetched once and kept fresh for ten minutes rather than joined per
 * card: the list is short, closed, and identical for every tenant.
 */
export function useCatalogUnitsQuery(): UseQueryResult<UnitIndex, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'units-of-measure'],
    queryFn: async ({ signal }) => {
      const page = await api.get<UnitOfMeasurePage>(`${CATALOG}/units-of-measure`, {
        query: { limit: REFERENCE_LIMIT },
        signal,
      });
      return indexUnits(page.items);
    },
    staleTime: 10 * 60_000,
    enabled,
  });
}

/**
 * Alternative units for one catalogue item.
 *
 * Reads `/v1/catalog/items/{sku_id}/conversions`, which is gated by `CATALOG_READ` — the
 * same permission as the product itself, so operator and buyer see the equivalences.
 * This previously read the backoffice reference route, which excludes exactly those two
 * profiles, so the card degraded for the people the catalogue exists for.
 *
 * The failure is still scoped to the card rather than the page: an operator told the
 * equivalence could not be loaded is better served than one shown an empty list, which
 * reads as "this product has no alternative packaging".
 */
export function useUnitConversionsQuery(
  skuId: string | undefined,
): UseQueryResult<readonly UnitConversion[], Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.catalogBrowse(key), 'unit-conversions', skuId ?? ''],
    queryFn: async ({ signal }) => {
      const response = await api.get<UnitConversionList>(
        `/v1/catalog/items/${encodeURIComponent(skuId ?? '')}/conversions`,
        { signal },
      );
      return response.conversions;
    },
    // A 404 here means the SKU left the caller's catalogue, which retrying cannot fix.
    retry: false,
    enabled: enabled && skuId !== undefined && skuId !== '',
  });
}
